import React, { useEffect, useMemo, useState } from 'react';
import { driverApi, DriverMe, DriverUser } from './services/driverApi';
import { DriverLoginPage } from './pages/driver/DriverLoginPage';
import { DriverDocumentsPage } from './pages/driver/DriverDocumentsPage';
import { DriverDocumentPage } from './pages/driver/DriverDocumentPage';
import { DriverMenuPage } from './pages/driver/DriverMenuPage';
import { DriverBalancePage } from './pages/driver/DriverBalancePage';
import { DriverHelpPage } from './pages/driver/DriverHelpPage';
import { COPY, DriverLang, getDriverLang, setDriverLang, shortDisplayName } from './pages/driver/driverI18n';
import './styles/cashalot.css';

type Screen = 'docs' | 'doc' | 'menu' | 'balance' | 'help';

const PENDING_DOC_KEY = 'driverOpenDocumentId';
const DOCUMENT_ID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const PAY_NOTICE_KEY = 'driverPayNotice';

function persistDocumentFromQuery(): void {
  try {
    const params = new URLSearchParams(window.location.search);
    const fromQuery = params.get('document') || '';
    if (!DOCUMENT_ID_RE.test(fromQuery)) return;
    sessionStorage.setItem(PENDING_DOC_KEY, fromQuery);
    params.delete('document');
    const next = params.toString();
    const path = window.location.pathname;
    window.history.replaceState({}, '', next ? `${path}?${next}` : path);
  } catch {
    /* ignore */
  }
}

function persistPayFromQuery(): 'ok' | 'fail' | 'cancel' | null {
  try {
    const params = new URLSearchParams(window.location.search);
    const pay = params.get('pay') || '';
    const notice = pay === 'ok' || pay === 'fail' || pay === 'cancel' ? pay : null;
    if (notice) {
      sessionStorage.setItem(PAY_NOTICE_KEY, notice);
      params.delete('pay');
      const next = params.toString();
      const path = window.location.pathname;
      window.history.replaceState({}, '', next ? `${path}?${next}` : path);
    }
    const stored = sessionStorage.getItem(PAY_NOTICE_KEY);
    return stored === 'ok' || stored === 'fail' || stored === 'cancel' ? stored : null;
  } catch {
    return null;
  }
}

function peekPendingDocumentId(): string | null {
  try {
    const pending = sessionStorage.getItem(PENDING_DOC_KEY);
    if (!pending || !DOCUMENT_ID_RE.test(pending)) return null;
    return pending;
  } catch {
    return null;
  }
}

function clearPendingDocumentId(id?: string | null): void {
  try {
    if (!id || sessionStorage.getItem(PENDING_DOC_KEY) === id) {
      sessionStorage.removeItem(PENDING_DOC_KEY);
    }
  } catch {
    /* ignore */
  }
}

export const DriverApp: React.FC = () => {
  const [user, setUser] = useState<DriverUser | null>(driverApi.getStoredUser());
  const [profile, setProfile] = useState<DriverMe | null>(null);
  const [lang, setLang] = useState<DriverLang>(getDriverLang);
  const [screen, setScreen] = useState<Screen>('docs');
  const [docId, setDocId] = useState<string | null>(null);
  const [payNotice, setPayNotice] = useState<'ok' | 'fail' | 'cancel' | null>(null);
  const t = useMemo(() => COPY[lang], [lang]);

  useEffect(() => {
    persistDocumentFromQuery();
    const notice = persistPayFromQuery();
    if (notice) {
      setPayNotice(notice);
      setScreen('balance');
    }
  }, []);

  useEffect(() => {
    document.documentElement.lang = lang === 'kk' ? 'kk' : 'ru';
  }, [lang]);

  useEffect(() => {
    if (!user) return;
    const pending = peekPendingDocumentId();
    if (pending) {
      setDocId(pending);
      setScreen('doc');
    }
  }, [user]);

  useEffect(() => {
    if (!user) return;
    driverApi
      .getMe()
      .then((me) => {
        const fio = me.fio || user.fio;
        setProfile({ ...me, fio });
        if (fio && fio !== user.fio) {
          const token = localStorage.getItem('driverToken');
          if (token) driverApi.setSession({ iin: me.iin, fio }, token);
        }
      })
      .catch(() => {
        setProfile({ iin: user.iin, fio: user.fio, balance: 0, topups: [] });
      });
  }, [user, payNotice]);

  const handleLogout = () => {
    driverApi.clearSession();
    setUser(null);
    setProfile(null);
    setScreen('docs');
    setDocId(null);
  };

  if (!user) {
    return (
      <DriverLoginPage
        t={t}
        lang={lang}
        onLang={(next) => {
          setLang(next);
          setDriverLang(next);
        }}
        onSuccess={() => {
          setUser(driverApi.getStoredUser());
        }}
      />
    );
  }

  const me: DriverMe = profile
    ? { ...profile, fio: profile.fio || user.fio }
    : { iin: user.iin, fio: user.fio, balance: 0, topups: [] };

  if (screen === 'doc' && docId) {
    return (
      <DriverDocumentPage
        t={t}
        id={docId}
        billing={me.billing}
        onPay={() => setScreen('balance')}
        onBack={() => {
          clearPendingDocumentId(docId);
          setScreen('docs');
          setDocId(null);
        }}
      />
    );
  }
  if (screen === 'menu') {
    return (
      <DriverMenuPage
        t={t}
        lang={lang}
        profile={me}
        onBack={() => setScreen('docs')}
        onLang={(next) => {
          setLang(next);
          setDriverLang(next);
        }}
        onBalance={() => setScreen('balance')}
        onHelp={() => setScreen('help')}
        onLogout={handleLogout}
      />
    );
  }
  if (screen === 'balance') {
    return (
      <DriverBalancePage
        t={t}
        profile={me}
        payNotice={payNotice}
        onBack={() => setScreen('menu')}
        onPaid={() => {
          driverApi.getMe().then(setProfile).catch(() => undefined);
        }}
      />
    );
  }
  if (screen === 'help') {
    return <DriverHelpPage t={t} onBack={() => setScreen('menu')} />;
  }

  return (
    <DriverDocumentsPage
      t={t}
      userName={shortDisplayName(me.fio)}
      billing={me.billing}
      onMenu={() => setScreen('menu')}
      onPay={() => setScreen('balance')}
      onOpenDoc={(id) => {
        setDocId(id);
        setScreen('doc');
      }}
    />
  );
};
